import { Toggle } from '@base-ui/react/toggle'
import { ToggleGroup } from '@base-ui/react/toggle-group'
import type { ReactNode } from 'react'
import { cn } from '../../lib/cn'
import s from './SegmentedControl.module.css'

/**
 * Contrôle segmenté — le sélecteur de vues du prototype (`.seg`) : une rangée de
 * segments dont un seul est enfoncé.
 *
 * Sur la primitive `ToggleGroup` de Base UI : `aria-pressed`, focus flèches, activation
 * clavier. L'état enfoncé se lit sur le `data-pressed` qu'elle publie — jamais une
 * classe. La primitive autorise de dé-presser le segment actif (groupe vide) ; ici un
 * segment est **toujours** choisi, donc ce cas est ignoré plutôt que remonté.
 */
export interface SegmentedControlOption<T extends string> {
  value: T
  label: ReactNode
  /**
   * Compteur affiché à droite du libellé.
   *
   * ⚠️ **Absent n'est pas zéro** : sans compteur, le segment n'en affiche aucun — écrire `0`
   * affirmerait qu'il n'y a rien.
   */
  count?: number
  /**
   * Segment présent mais inactionnable, quand le prototype le prévoit et que la donnée qu'il
   * filtre n'existe pas encore. Le retirer masquerait le manque, l'afficher actif mentirait.
   */
  disabled?: boolean
}

export interface SegmentedControlProps<T extends string> {
  options: readonly SegmentedControlOption<T>[]
  value: T
  onValueChange: (value: T) => void
  /** Nom accessible du groupe (« Vues »…). */
  label: string
  /**
   * Le choix se lit mais ne se change plus — un contrôle transmis est immuable côté terrain
   * (D4).
   *
   * Désactivé plutôt que masqué : ce que l'agent doit voir, c'est la valeur retenue. La
   * retirer de l'écran l'obligerait à se souvenir de ce qu'il a conclu.
   */
  disabled?: boolean
  className?: string
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onValueChange,
  label,
  disabled,
  className,
}: SegmentedControlProps<T>) {
  return (
    <div className={s.scroller}>
      <ToggleGroup
        value={[value]}
        onValueChange={(next: T[]) => {
          const [chosen] = next
          if (chosen !== undefined) onValueChange(chosen)
        }}
        disabled={disabled}
        aria-label={label}
        className={cn(s.root, className)}
      >
        {options.map((option) => (
          <Toggle
            key={option.value}
            value={option.value}
            disabled={option.disabled}
            className={s.segment}
          >
            {option.label}
            {option.count !== undefined && (
              <>
                {/*
                 * Espace explicite avant le compteur. Le nom accessible d'un bouton concatène
                 * le texte de ses descendants **inline** sans séparateur : sans lui, un
                 * lecteur d'écran annonce « Toutes3 » — un mot inventé — au lieu de
                 * « Toutes 3 ». L'espace du `gap` est visuel, il n'entre pas dans le calcul.
                 */}{' '}
                <span className={s.count}>{option.count}</span>
              </>
            )}
          </Toggle>
        ))}
      </ToggleGroup>
    </div>
  )
}
